import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { useState } from 'react'
import { useRouter } from 'next/router'
import { RefreshCw, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

interface EmptyStateProps {
    icon: LucideIcon
    title: string
    description: string
    action?: ReactNode
    className?: string
}

export const EmptyState: FC<EmptyStateProps> = ({ icon: Icon, title, description, action, className }): ReactNode => {
    return (
        <div className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
            <span className='mb-4 flex size-12 items-center justify-center rounded-full bg-secondary text-muted-foreground'>
                <Icon aria-hidden='true' className='size-6' />
            </span>

            <h3 className='font-heading text-base font-semibold text-foreground'>
                {title}
            </h3>

            <p className='mt-1 max-w-sm text-sm text-muted-foreground'>
                {description}
            </p>

            {action && <div className='mt-5'>{action}</div>}
        </div>
    )
}

interface ErrorStateProps {
    title?: string
    description?: string
}

export const ErrorState: FC<ErrorStateProps> = ({
    title = "We couldn't load this page",
    description = 'Your data is safe. The connection dropped for a moment, so try loading it again.'
}): ReactNode => {
    const router = useRouter()
    const [retrying, setRetrying] = useState(false)

    const retry = async () => {
        setRetrying(true)

        try {
            await router.replace(router.asPath, undefined, { scroll: false })
        }
        finally {
            setRetrying(false)
        }
    }

    return (
        <div role='alert' className='rounded-xl border border-border bg-card px-6 py-16 text-center'>
            <span className='mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-danger-soft text-danger'>
                <TriangleAlert aria-hidden='true' className='size-6' />
            </span>

            <h2 className='font-heading text-lg font-semibold text-foreground'>
                {title}
            </h2>

            <p className='mx-auto mt-1 max-w-md text-sm text-muted-foreground'>
                {description}
            </p>

            <Button onClick={() => void retry()} disabled={retrying} className='mt-6 h-9 px-3'>
                <RefreshCw aria-hidden='true' className={cn('size-4', retrying && 'animate-spin')} />
                {retrying ? 'Loading' : 'Try again'}
            </Button>
        </div>
    )
}
